<template>
  <div class="h-100 d-flex flex-column">
    <b-row v-if="title" class="mb-3">
      <b-col class="text-left">
        <h4>{{ title }}</h4>
      </b-col>
      <b-col
        cols="4"
        align-self="center"
        class="text-right"
        v-if="!hideCreateAction"
      >
        <b-button variant="outline-primary" @click="createRow">
          <i class="fa fa-plus" />
          {{ addItemLabel }}</b-button
        >
      </b-col>
    </b-row>
    <Table
      id="table"
      class="p-0 text-left flex-grow-1"
      :items="items"
      :fields="fields"
      :filterable="true"
      :selectable="true"
      :current-page="currentPage"
      :per-page="perPage"
      :total-rows="totalRows"
      :stickyHeader="stickyHeader"
      @sort-changed="onSortChanged"
      @filtered="onFilterChanged"
      @page-changed="onPageChanged"
      @per-page-changed="onPerPageChanged"
    >
      <template v-slot:cell(actions)="data">
        <div class="actionsContainer text-center">
          <a
            v-if="
              showOnMapAction &&
                get(data, 'item.measures.position.point', false)
            "
            href="#"
            @click.prevent="
              $emit('show-on-map', data.item.measures.position.point)
            "
            class="text-primary"
            v-b-tooltip.hover.bottom="'Show on map'"
          >
            <i class="pointer fa fa-map" /> </a
          ><a
            v-if="!hideEditAction"
            href="#"
            @click.prevent="editRow(data)"
            class="text-primary"
            v-b-tooltip.hover.bottom="'Edit'"
          >
            <i class="pointer fa fa-pen"/></a
          ><a
            v-if="!hideDeleteAction"
            href="#"
            @click.prevent="removeRow(data)"
            class="text-danger"
            v-b-tooltip.hover.bottom="'Delete'"
          >
            <i class="pointer fa fa-trash" /> </a
          ><a
            v-if="includeGlobalIndex"
            v-b-tooltip.hover.bottom="'Attach'"
            class="text-primary"
            href="#"
            :data-cy="`Table-attachBtn--${data.item.reference}`"
            @click.prevent="attach(data)"
          >
            <i class="pointer fa fa-link" /> </a
          ><a
            v-if="showDetachAction"
            href="#"
            @click.prevent="detach(data)"
            class="text-primary"
            v-b-tooltip.hover.bottom="'Detach'"
          >
            <i class="pointer fa fa-unlink" />
          </a>
          <router-link
            :data-cy="`Table-infoBtn--${data.item.reference}`"
            :to="`/${dataType}/${data.item._id}`"
            class="text-primary"
            v-b-tooltip.hover.bottom="'Info'"
          >
            <i class="pointer fa fa-info-circle" />
          </router-link>
        </div>
      </template>
      <template v-slot:cell(measures)="data">
        <template v-if="data.item.measures">
          <div
            v-for="measureType of Object.keys(data.item.measures)"
            :key="measureType"
          >
            <b-badge
              class="mr-1"
              :id="`${data.item.reference}-${measureType}`"
              v-b-tooltip.hover.right="
                `${defaultTo(
                  getMeasureByType(
                    data.item.measures[measureType],
                    measureType
                  ),
                  'N/A'
                )}`
              "
              >{{ measureType }}</b-badge
            >
          </div>
        </template>
      </template>
      <template v-slot:cell(assetId)="data">
        <router-link
          v-if="data.item.assetId"
          :to="{ name: 'asset', params: { assetId: data.item.assetId } }"
          >{{ data.item.assetId }}</router-link
        >
        <span v-else>No asset assigned</span>
      </template>
      <template #empty>
        <h4 class="text-center mt-3">There is currently no results.</h4>
        <p class="text-center">
          Try changing the filters to modify your search.
        </p>
      </template>
    </Table>

    <b-modal
      :id="createUpdateModalUid"
      :title="modalAction"
      @hide="modalHidden"
      size="lg"
    >
      <vue-form-generator :schema="formSchema" :model="document">
      </vue-form-generator>
    </b-modal>

    <b-modal
      data-cy="AttachToTenantModal"
      title="Attach to tenant"
      :id="attachModalUid"
      @hide="attachModalHidden"
    >
      <b-form-select
        v-model="attachToTenant"
        :options="
          tenants.map(tenant => ({
            value: tenant._id,
            text: tenant.name
          }))
        "
      ></b-form-select>
    </b-modal>
  </div>
</template>

<script>
require('ace-builds');
require('ace-builds/webpack-resolver');

import { ref, reactive, watch } from '@vue/composition-api';
import VueFormGenerator from 'vue-form-generator';

import { FormSchemaService } from '@/components/vue-shared-components';
import { mapGetters } from 'vuex';
import { getMeasureByType } from '@/helpers/measures';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';

export default {
  name: 'TableView',
  components: {
    VueFormGenerator: VueFormGenerator.component
  },
  props: {
    index: {
      type: String,
      required: true
    },
    controller: {
      type: String,
      required: true
    },
    collection: {
      type: String,
      required: true
    },
    addItemLabel: {
      type: String,
      required: false,
      default: 'Add an item'
    },
    title: {
      type: String,
      required: false
    },
    includeGlobalIndex: {
      type: Boolean,
      required: false,
      default: false
    },
    hideCreateAction: {
      type: Boolean,
      required: false,
      default: false
    },
    hideEditAction: {
      type: Boolean,
      required: false,
      default: false
    },
    hideDeleteAction: {
      type: Boolean,
      required: false,
      default: false
    },
    showDetachAction: {
      type: Boolean,
      required: false,
      default: false
    },
    dataType: {
      type: String,
      required: true,
      default: 'asset',
      validator: val => ['asset', 'device'].includes(val)
    },
    showMapAction: {
      type: Boolean,
      required: false,
      default: false
    },
    showOnMapAction: {
      type: Boolean,
      required: false,
      default: false
    },
    stickyHeader: {
      required: false,
      default: true
    }
  },
  computed: {
    ...mapGetters('tenants', ['tenants'])
  },
  setup(props, ctx) {
    const createUpdateModalUid = Date.now().toString();
    const attachModalUid = (Date.now() + Math.random()).toString();
    const items = reactive([]);
    let totalRows = ref(0);
    let currentPage = 1;
    let perPage = ref(10);
    let filter = ref('');
    let sort = ref(null);
    let formSchema = ref({});
    let fields = ref([]);
    let document = ref({});
    let modalAction = ref('');
    let attachToTenant = ref('');
    let deviceToAttach = ref({});

    const fetchItems = async bounds => {
      let query = {};
      if (filter.value.length) {
        query = {
          query: {
            bool: {
              should: [
                {
                  bool: {
                    must: {
                      multi_match: {
                        query: filter.value,
                        type: 'phrase_prefix',
                        fields: ['model.text', 'reference.text', 'type.text']
                      }
                    }
                  }
                }
              ]
            }
          }
        };
      } else {
        query.query = {
          bool: { should: [{ bool: {} }] }
        };
      }
      if (sort.value) {
        query.sort = sort.value;
      }
      if (props.includeGlobalIndex) {
        query.query.bool.should[0].bool.must_not = {
          exists: {
            field: 'tenantId'
          }
        };
      }
      if (bounds) {
        query.query.bool.filter = {
          geo_bounding_box: {
            ['measures.position.point']: {
              top_left: {
                lat: bounds._northEast.lat,
                lon: bounds._southWest.lng
              },
              bottom_right: {
                lat: bounds._southWest.lat,
                lon: bounds._northEast.lng
              }
            }
          }
        };
      }
      const res = await ctx.root.$kuzzle.query({
        controller: props.includeGlobalIndex ? 'document' : props.controller,
        action: 'search',
        collection: props.collection,
        index: props.index,
        body: {
          query: query.query,
          sort: query.sort
        },
        from: (currentPage - 1) * perPage.value,
        size: perPage.value
      });

      let result = res.result;

      items.splice(items, items.length);
      for (const doc of result.hits) {
        items.push({
          _id: doc._id,
          ...doc._source
        });
      }
      ctx.emit('fetched', items);
      totalRows.value = result.total;

      const mappings = await ctx.root.$kuzzle.collection.getMapping(
        props.index,
        props.collection,
        {
          includeKuzzleMeta: false
        }
      );
      fields.value.push(
        {
          key: 'type',
          label: 'Type',
          sortable: true
        },
        {
          key: 'model',
          label: 'Model',
          sortable: true
        },
        {
          key: 'reference',
          label: 'Reference',
          sortable: true
        },
        {
          key: 'measures',
          label: 'Measures',
          sortable: true
        }
      );

      if (!props.includeGlobalIndex) {
        if (props.dataType === 'device') {
          fields.value.push({
            key: 'assetId',
            label: 'Asset',
            sortable: true
          });
        }
      }

      fields.value.push({
        key: 'actions',
        label: 'Actions',
        sortable: false,
        stickyColumn: true,
        thStyle: {
          minWidth: '75px'
        }
      });

      const formSchemaService = new FormSchemaService();
      formSchema.value = formSchemaService.generate(
        mappings.properties,
        document.value
      );
    };

    watch(
      () => [props.index, props.collection],
      () => fetchItems()
    );

    fetchItems();

    return {
      get,
      createUpdateModalUid,
      getMeasureByType,
      attachModalUid,
      attachToTenant,
      items,
      currentPage,
      perPage,
      totalRows,
      fields,
      formSchema,
      document,
      modalAction,
      defaultTo,
      fetchItems,
      editRow: data => {
        document.value = JSON.parse(JSON.stringify(data.item));
        modalAction.value = 'Edit';
        ctx.root.$bvModal.show(createUpdateModalUid);
      },
      createRow: data => {
        document.value = {};
        modalAction.value = 'Create';
        ctx.root.$bvModal.show(createUpdateModalUid);
      },
      detach: async data => {
        try {
          await ctx.root.$kuzzle.query(
            {
              controller: props.controller,
              action: 'detach',
              _id: data.item._id
            },
            {
              refresh: 'wait_for'
            }
          );
          fetchItems();
          ctx.emit('detached', {
            tenant: attachToTenant.value,
            deviceId: deviceToAttach.value._id
          });
        } catch (e) {
          ctx.root.$bvToast.toast(`${e}`, {
            title: 'Error',
            autoHideDelay: 5000,
            variant: 'danger',
            appendToast: true
          });
        }
      },
      attach: data => {
        deviceToAttach.value = JSON.parse(JSON.stringify(data.item));
        ctx.root.$bvModal.show(attachModalUid);
      },
      attachModalHidden: async event => {
        if (event.trigger === 'ok') {
          try {
            await ctx.root.$kuzzle.query(
              {
                index: attachToTenant.value,
                controller: props.controller,
                action: 'attachTenant',
                _id: deviceToAttach.value._id
              },
              {
                refresh: 'wait_for'
              }
            );
            fetchItems();
            ctx.emit('attached', {
              tenant: attachToTenant.value,
              deviceId: deviceToAttach.value._id
            });
          } catch (e) {
            ctx.root.$bvToast.toast(`${e}`, {
              title: 'Error',
              autoHideDelay: 5000,
              variant: 'danger',
              appendToast: true
            });
          }
        }
        attachToTenant.value = '';
        deviceToAttach.value = {};
      },
      modalHidden: async event => {
        if (event.trigger === 'ok') {
          if (document.value && document.value._id) {
            const id = document.value._id;
            delete document.value._id;
            try {
              await ctx.root.$kuzzle.query(
                {
                  index: props.index,
                  controller: props.controller,
                  action: 'update',
                  _id: id,
                  body: document.value
                },
                {
                  refresh: 'wait_for'
                }
              );
            } catch (e) {
              ctx.root.$bvToast.toast(`${e}`, {
                title: 'Error',
                autoHideDelay: 5000,
                variant: 'danger',
                appendToast: true
              });
            }
          } else {
            try {
              await ctx.root.$kuzzle.query(
                {
                  index: props.index,
                  controller: props.controller,
                  action: 'create',
                  body: document.value
                },
                {
                  refresh: 'wait_for'
                }
              );
            } catch (e) {
              ctx.root.$bvToast.toast(`${e}`, {
                title: 'Error',
                autoHideDelay: 5000,
                variant: 'danger',
                appendToast: true
              });
            }
          }

          fetchItems();
        }
      },
      removeRow: data => {
        ctx.root.$bvModal.msgBoxConfirm('Are you sure?').then(async value => {
          if (value) {
            try {
              await ctx.root.$kuzzle.query(
                {
                  index: props.index,
                  controller: props.controller,
                  action: 'delete',
                  _id: data.item._id
                },
                {
                  refresh: 'wait_for'
                }
              );
            } catch (e) {
              ctx.root.$bvToast.toast(`${e}`, {
                title: 'Error',
                autoHideDelay: 5000,
                variant: 'danger',
                appendToast: true
              });
            }
          }
          fetchItems();
        });
      },
      onPageChanged: page => {
        currentPage = page;
        fetchItems();
      },
      onPerPageChanged: value => {
        perPage.value = value;
        currentPage = 1;
        fetchItems();
      },
      onFilterChanged: receivedFilter => {
        filter.value = receivedFilter;
        fetchItems();
      },
      onSortChanged: data => {
        sort.value = [
          {
            [data.sortBy]: {
              order: data.sortDesc ? 'desc' : 'asc'
            }
          }
        ];
        fetchItems();
      }
    };
  }
};
</script>

<style lang="sass" scoped>
.pointer
  cursor: pointer

.actionsContainer
  width: 80px
  a
    margin-right: 5px
</style>
